import React from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import type { VocabularyRef } from '@semantical/modules/semantical-core';

type Props = {
    selectedVocabularyUri: string | null;
    availableVocabularies: VocabularyRef[];
    onVocabularyChange: (uri: string | null) => void;
    darkMode?: boolean;
};

type State = {
    isOpen: boolean;
    dropdownPosition: { top: number; left: number; width: number } | null;
};

/**
 * LabelingContextSelector - Per-diagram dropdown for selecting the labeling context (SKOS vocabulary).
 *
 * Populated from the BoundedContext's Ubiquitous Language vocabularies (imported + local).
 * When a vocabulary is selected, it provides terminology guidance for labeling elements.
 * When "None" is selected, there is no terminology guidance.
 */
export default class LabelingContextSelector extends React.Component<Props, State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private buttonRef = React.createRef<HTMLButtonElement>();

    state: State = {
        isOpen: false,
        dropdownPosition: null,
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
            // Also check if click is inside the portal dropdown
            const dropdown = document.getElementById('labeling-context-selector-dropdown');
            if (dropdown && dropdown.contains(event.target as Node)) {
                return;
            }
            this.setState({ isOpen: false });
        }
    };

    handleToggle = () => {
        if (!this.state.isOpen && this.buttonRef.current) {
            const rect = this.buttonRef.current.getBoundingClientRect();
            this.setState({
                isOpen: true,
                dropdownPosition: {
                    top: rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                },
            });
        } else {
            this.setState({ isOpen: false });
        }
    };

    handleSelect = (uri: string | null) => {
        this.props.onVocabularyChange(uri);
        this.setState({ isOpen: false });
    };

    render() {
        const { selectedVocabularyUri, availableVocabularies } = this.props;
        const { isOpen, dropdownPosition } = this.state;

        const selectedVocabulary = availableVocabularies.find(v => v.uri === selectedVocabularyUri);

        const dropdown = isOpen && dropdownPosition ? (
            <div
                id="labeling-context-selector-dropdown"
                className="fixed bg-surface-light dark:bg-[rgba(28,30,36,0.98)] border border-slate-200 dark:border-accent-border/20 rounded-lg shadow-[0_8px_32px_rgba(100,120,140,0.15)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden z-[10001] backdrop-blur-[12px]"
                style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    minWidth: dropdownPosition.width,
                }}
            >
                {/* None option - no labeling guidance */}
                <div
                    className={clsx(
                        "px-3 py-2.5 text-[13px] cursor-pointer transition-all duration-100 flex items-center gap-2",
                        selectedVocabularyUri === null
                            ? "bg-accent-teal/10 dark:bg-accent/15 text-accent-teal dark:text-accent font-semibold"
                            : "text-slate-500 dark:text-white/60 bg-transparent hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10"
                    )}
                    onClick={() => this.handleSelect(null)}
                >
                    {selectedVocabularyUri === null && (
                        <svg width="14" height="14" viewBox="0 0 14 14">
                            <path d="M2,7 L5,10 L12,3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                    {selectedVocabularyUri !== null && <div className="w-3.5" />}
                    <span className="italic">None</span>
                </div>

                {availableVocabularies.length > 0 && (
                    <div className="border-t border-slate-200 dark:border-accent-border/20 my-1" />
                )}

                {availableVocabularies.map(vocabulary => {
                    const isSelected = vocabulary.uri === selectedVocabularyUri;
                    return (
                        <div
                            key={vocabulary.uri}
                            className={clsx(
                                "px-3 py-2.5 text-[13px] cursor-pointer transition-all duration-100 flex items-center gap-2",
                                isSelected
                                    ? "bg-accent-teal/10 dark:bg-accent/15 text-accent-teal dark:text-accent font-semibold"
                                    : "text-slate-700 dark:text-white/85 bg-transparent hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10"
                            )}
                            onClick={() => this.handleSelect(vocabulary.uri)}
                        >
                            {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 14 14">
                                    <path d="M2,7 L5,10 L12,3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            {!isSelected && <div className="w-3.5" />}
                            <span>{vocabulary.label}</span>
                            <span className={clsx(
                                "text-[10px] px-1.5 py-0.5 rounded",
                                vocabulary.source === 'imported'
                                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300"
                                    : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300"
                            )}>
                                {vocabulary.source}
                            </span>
                        </div>
                    );
                })}
            </div>
        ) : null;

        return (
            <div ref={this.containerRef} className="relative inline-block ml-3 font-sans">
                <button
                    ref={this.buttonRef}
                    className={clsx(
                        "flex items-center gap-2 px-3 py-2 text-[13px] font-medium rounded-lg border cursor-pointer outline-none transition-all duration-150 min-w-[160px] justify-between",
                        isOpen
                            ? "bg-slate-50 dark:bg-accent/10 border-accent-teal dark:border-accent/40 text-slate-700 dark:text-white"
                            : "bg-white dark:bg-white/[0.06] border-slate-200 dark:border-accent-border text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-accent/10 hover:border-accent-teal dark:hover:border-accent/40"
                    )}
                    onClick={this.handleToggle}
                    title="Select labeling context (vocabulary)"
                >
                    <span className={!selectedVocabulary ? "text-slate-400 dark:text-white/50 italic" : ""}>
                        {selectedVocabulary?.label || 'No Labeling'}
                    </span>
                    <svg
                        className={clsx(
                            "transition-transform duration-200 opacity-60",
                            isOpen && "rotate-180"
                        )}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                    >
                        <path d="M3,4 L6,7 L9,4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                {dropdown && ReactDOM.createPortal(dropdown, document.body)}
            </div>
        );
    }
}
